// ===========================================
// Password Confirmation Modal
// ===========================================
// Prompts user to verify password before sensitive operations
// (viewing env vars, editing credentials, etc.)

import React, { useState } from 'react';
import { Lock, X } from 'lucide-react';
import { authAPI } from '../../services/api';

const PasswordModal = ({ 
  action,        // 'view' | 'edit' | 'delete' | 'delete-project'
  envVar,        // The env variable or project being acted upon (optional)
  onSuccess,     // Callback when password is verified
  onClose        // Callback to close modal
}) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Handle password verification
  const handleVerify = async () => {
    if (!password.trim()) {
      setError('Password is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Use dedicated verify-password endpoint
      const response = await authAPI.verifyPassword({ password });

      if (response.data.success) {
        // Password verified - trigger success callback
        onSuccess();
        onClose();
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Incorrect password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleVerify();
    }
  };

  // Generate action-specific message based on operation type
  const getMessage = () => {
    switch (action) {
      case 'view':
        return 'Enter your password to unlock and view all environment variable values.';
      case 'edit':
        return `Enter your password to edit "${envVar?.key}".`;
      case 'delete':
        return `Enter your password to delete "${envVar?.key}". This action cannot be undone.`;
      case 'delete-project':
        // Special case for permanent project deletion
        return `Enter your password to permanently delete "${envVar?.key}". This action cannot be undone and all project data will be lost.`;
      default:
        return 'Enter your password to continue.';
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal--sm" onClick={e => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal__header">
          <h2>
            <Lock size={20} />
            Confirm Password
          </h2>
          <button className="modal__close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Modal Content */}
        <div className="modal__form">
          <p className="modal__text">{getMessage()}</p>
          
          {/* Error Display */}
          {error && (
            <div className="modal__error">{error}</div>
          )}
          
          {/* Password Input */}
          <div className="modal__field">
            <label htmlFor="confirmPassword">Password</label>
            <input
              id="confirmPassword"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError('');
              }}
              onKeyDown={handleKeyDown}
              disabled={loading}
              autoFocus
            />
          </div>
          
          {/* Action Buttons */}
          <div className="modal__actions">
            <button 
              className="btn-secondary" 
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              className={`btn-primary ${(action === 'delete' || action === 'delete-project') ? 'btn-danger' : ''}`}
              onClick={handleVerify}
              disabled={loading}
            >
              {loading ? 'Verifying...' : (
                action === 'delete-project' ? 'Delete Project' :
                action === 'delete' ? 'Delete Variable' : 'Confirm'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordModal;

